/*
  Kyrö showcase page: scroll effects for the kyro-sc-* sections.
  - [data-sc-reveal]        fade/slide in when entering the viewport
  - [data-sc-words]         split into words that slide up one by one
  - [data-sc-scrub]         words brighten progressively as the block scrolls through
  - [data-sc-parallax="n"]  moves at n × scroll speed relative to the viewport centre
  - [data-sc-chapters]      sticky media that follows the active chapter
  Respects prefers-reduced-motion and re-initialises in the theme editor.
*/
(() => {
  if (window.kyroShowcase) return;

  const root = document.documentElement;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  root.classList.add('kyro-sc-anim');

  const splitWords = (el, wrap) => {
    if (el.dataset.scSplit) return;
    el.dataset.scSplit = '1';
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    let i = 0;
    nodes.forEach((node) => {
      const frag = document.createDocumentFragment();
      node.textContent.split(/(\s+)/).forEach((part) => {
        if (!part) return;
        if (/^\s+$/.test(part)) {
          frag.appendChild(document.createTextNode(part));
          return;
        }
        frag.appendChild(wrap(part, i++));
      });
      node.parentNode.replaceChild(frag, node);
    });
  };

  const revealWord = (text, i) => {
    const outer = document.createElement('span');
    outer.className = 'kyro-sc-word';
    const inner = document.createElement('span');
    inner.textContent = text;
    inner.style.setProperty('--sc-i', i);
    outer.appendChild(inner);
    return outer;
  };

  const scrubWord = (text) => {
    const span = document.createElement('span');
    span.className = 'kyro-sc-scrub-word';
    span.textContent = text;
    return span;
  };

  // ---------- Reveal ----------
  const revealObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        revealObserver.unobserve(entry.target);
      });
    },
    // Phones: start the reveal already when the element is just below the screen, so fast swipes never show empty panels
    window.matchMedia('(max-width: 768px)').matches ? { rootMargin: '0px 0px 25% 0px', threshold: 0 } : { rootMargin: '0px 0px -12% 0px', threshold: 0.12 }
  );

  // ---------- Scroll-driven effects (parallax, scrub, chapters) ----------
  let parallaxEls = [];
  const phoneMq = window.matchMedia('(max-width: 768px)');
  let scrubEls = [];
  let chapterGroups = [];
  let headerEls = [];
  let headerOffset = -1;
  let ticking = false;

  // The theme header sticks to the top while scrolling; expose how far it reaches down
  // as --kyro-sc-header so pinned showcase elements can sit just below it.
  const findHeaderEls = () => {
    headerEls = Array.from(document.querySelectorAll('#HeaderWrapper, #HeaderWrapper *, header-section, header-section *')).filter((el) => {
      const pos = getComputedStyle(el).position;
      return pos === 'fixed' || pos === 'sticky';
    });
  };

  const measureHeader = () => {
    let bottom = 0;
    headerEls.forEach((el) => {
      const rect = el.getBoundingClientRect();
      if (rect.top <= 1 && rect.bottom > bottom) bottom = rect.bottom;
    });
    bottom = Math.round(bottom);
    if (bottom !== headerOffset) {
      headerOffset = bottom;
      root.style.setProperty('--kyro-sc-header', `${bottom}px`);
    }
  };

  const update = () => {
    ticking = false;
    const vh = window.innerHeight;
    measureHeader();

    if (!reduceMotion.matches) {
      // no parallax on phones: moving photos during touch (momentum) scrolling only costs smoothness there
      if (!phoneMq.matches) parallaxEls.forEach((el) => {
        const rect = el.parentElement.getBoundingClientRect();
        if (rect.bottom < -200 || rect.top > vh + 200) return;
        const speed = parseFloat(el.dataset.scParallax);
        if (!speed) return;
        const offset = (rect.top + rect.height / 2 - vh / 2) * -speed;
        el.style.setProperty('--sc-y', `${offset.toFixed(1)}px`);
      });

      scrubEls.forEach((el) => {
        const rect = el.getBoundingClientRect();
        // 0 when the block's top hits 85% of the viewport, 1 when its bottom reaches 45%.
        const start = vh * 0.85;
        const end = vh * 0.45;
        const total = start - end + rect.height;
        const progress = Math.min(1, Math.max(0, (start - rect.top) / total));
        const words = el._scWords;
        const n = words.length;
        words.forEach((w, i) => {
          const p = Math.min(1, Math.max(0, progress * n - i));
          w.style.setProperty('--sc-p', p.toFixed(2));
        });
      });
    }

    chapterGroups.forEach((group) => {
      let active = 0;
      group.chapters.forEach((ch, i) => {
        if (ch.getBoundingClientRect().top < vh * 0.55) active = i;
      });
      if (group.active === active) return;
      group.active = active;
      group.media.forEach((m, i) => m.classList.toggle('is-active', i === active));
      group.chapters.forEach((c, i) => c.classList.toggle('is-active', i === active));
    });
  };

  const requestUpdate = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(update);
  };

  // Opening scenes fill the screen minus what sits above them (announcement bar, header).
  // Measured from the document top so it stays stable while the header changes on scroll.
  const measureHeroOffsets = () => {
    document.querySelectorAll('.kyro-sc-hero').forEach((el) => {
      const top = Math.max(0, Math.round(el.getBoundingClientRect().top + window.scrollY));
      el.style.setProperty('--sc-offset', `${top}px`);
    });
  };

  // ---------- Tabs ([data-sc-tabs] > [data-sc-tab] + [data-sc-panel]) ----------
  // Without JS every panel stays visible; JS hides all but the active one.
  const activateTab = (group, index, focus = false) => {
    const tabs = Array.from(group.querySelectorAll('[data-sc-tab]'));
    const panels = Array.from(group.querySelectorAll('[data-sc-panel]'));
    if (!tabs[index]) return;
    tabs.forEach((tab, i) => {
      const on = i === index;
      tab.setAttribute('aria-selected', on ? 'true' : 'false');
      tab.tabIndex = on ? 0 : -1;
      tab.classList.toggle('is-active', on);
    });
    panels.forEach((panel, i) => {
      panel.hidden = i !== index;
      panel.classList.toggle('is-active', i === index);
    });
    if (focus) tabs[index].focus();
  };

  const initTabs = (scope) => {
    scope.querySelectorAll('[data-sc-tabs]').forEach((group) => {
      if (group.dataset.scTabsReady) return;
      group.dataset.scTabsReady = '1';
      const tabs = Array.from(group.querySelectorAll('[data-sc-tab]'));
      tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => activateTab(group, i));
        tab.addEventListener('keydown', (e) => {
          const keys = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
          if (e.key in keys) {
            e.preventDefault();
            activateTab(group, (i + keys[e.key] + tabs.length) % tabs.length, true);
          } else if (e.key === 'Home' || e.key === 'End') {
            e.preventDefault();
            activateTab(group, e.key === 'Home' ? 0 : tabs.length - 1, true);
          }
        });
      });
      activateTab(group, 0);
    });
  };

  // Theme editor: selecting a block shows its tab.
  document.addEventListener('shopify:block:select', (e) => {
    const panel = e.target.closest('[data-sc-panel]') || e.target;
    const group = panel.closest('[data-sc-tabs]');
    if (!group) return;
    const index = Array.from(group.querySelectorAll('[data-sc-panel]')).indexOf(panel);
    if (index > -1) activateTab(group, index);
  });

  const init = (scope = document) => {
    measureHeroOffsets();
    initTabs(scope);
    scope.querySelectorAll('[data-sc-words]').forEach((el) => { splitWords(el, revealWord); el.classList.add('is-split'); });
    scope.querySelectorAll('[data-sc-scrub]').forEach((el) => {
      splitWords(el, scrubWord);
      el._scWords = Array.from(el.querySelectorAll('.kyro-sc-scrub-word'));
    });
    scope.querySelectorAll('[data-sc-reveal], [data-sc-words]').forEach((el) => {
      if (reduceMotion.matches) el.classList.add('is-visible');
      else revealObserver.observe(el);
    });

    findHeaderEls();
    parallaxEls = Array.from(document.querySelectorAll('[data-sc-parallax]'));
    scrubEls = Array.from(document.querySelectorAll('[data-sc-scrub]'));
    chapterGroups = Array.from(document.querySelectorAll('[data-sc-chapters]')).map((el) => ({
      chapters: Array.from(el.querySelectorAll('[data-sc-chapter]')),
      media: Array.from(el.querySelectorAll('[data-sc-chapter-media]')),
      active: -1,
    }));
    requestUpdate();
  };

  window.addEventListener('scroll', requestUpdate, { passive: true });
  window.addEventListener('resize', () => {
    findHeaderEls();
    measureHeroOffsets();
    requestUpdate();
  });
  document.addEventListener('shopify:section:load', (e) => init(e.target));

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  window.kyroShowcase = { init };
})();
